/**
 * Indian mobile number helpers (shared by PhoneInput + form-level validation).
 * Rule: exactly 10 digits starting with 6/7/8/9, carried as E.164 `+91XXXXXXXXXX` — the API's
 * IndianPhone (api/app/schemas/v1/common.py).
 */

const LOCAL_RE = /^[6-9]\d{9}$/;

/**
 * A patient's phone as staff see it. A doctor gets the last 4 digits only (D-20), e.g.
 * "XXXXXXXXX3210", shown as "•••••• 3210"; a full "+919876543210" is spaced as "+91 98765 43210".
 */
export function phoneLabel(phone: string): string {
  const p = phone.replace(/\s+/g, "");
  const masked = p.match(/^X+(\d{4})$/);
  if (masked) return `•••••• ${masked[1]}`;
  const full = p.match(/^\+91(\d{5})(\d{5})$/);
  return full ? `+91 ${full[1]} ${full[2]}` : phone;
}

/** The digits of a number, without the 91 / 0 written in front of a whole one (spaces, dashes and
 *  a + dropped). A number is only read as 91… when it is 12 digits long: 9198765432 stays as is. */
function nationalDigits(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) return digits.slice(2);
  if (digits.length === 11 && digits.startsWith("0")) return digits.slice(1);
  return digits;
}

/**
 * What a local 10-digit field holds after typing or pasting (AUTH-16): a whole number pasted with
 * +91 / 91 / 0 in front becomes its 10 local digits; otherwise the digits, at most 10. Never the
 * first 10 of a longer number: "+91 98765 43210" is 9876543210, not 9198765432.
 */
export function typedLocal(raw: string): string {
  return nationalDigits(raw).slice(0, 10);
}

/** The local digits of a stored value: E.164 (`+91…`, possibly still being typed) or bare digits.
 *  Never shortened, so a number with too many digits stays invalid. */
export function localDigits(value: string): string {
  const v = value.trim();
  // An explicit +91 is the country code, even before all 10 digits are typed.
  return nationalDigits(v.startsWith("+91") ? v.slice(3) : v);
}

/** True when `value` (E.164 `+91…` or bare digits) is a valid Indian mobile number. */
export function isValidIndianPhone(value: string): boolean {
  return LOCAL_RE.test(localDigits(value));
}

/** What PhoneInput reports for raw typed or pasted text: `+91` + the local digits. Built on
    typedLocal (AUTH-16), so a pasted "+91 98765 43210" stays 9876543210 instead of keeping the
    first 10 digits ("+919198765432"). */
export function typedToE164(raw: string): string {
  return `+91${typedLocal(raw)}`;
}

/** True when the 10 local digits are a valid number (an optional field checks for empty itself). */
export function isValidLocal(local: string): boolean {
  return LOCAL_RE.test(local);
}
